import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "hooks/auth/useAuth";
import {
  ClientInvoiceData,
  IncreaseExternalAccountDetails,
  StakeholderPayment,
} from "@freetech/models/bank";
import {
  collection,
  QuerySnapshot,
  onSnapshot,
  getDocs,
} from "firebase/firestore";
import { db } from "@freetech/models/db";
import { apiClient } from "@/api/apiClient";
import { loadClientsNonAdmin } from "@/core/clients";
import { useSnackbar } from "@/hooks/common/useSnackbar";

export const useStakeholderPayments = () => {
  const { currentUser, role } = useAuth();
  if (!currentUser || !role) {
    throw new Error("No authenticated user");
  }

  const queryClient = useQueryClient();
  const { showError } = useSnackbar();
  const invalidatePaymentProfileQueries = async () => {
    if (!currentUser?.uid) return;

    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["stakeholder", currentUser.uid],
      }),
      queryClient.refetchQueries({
        queryKey: ["stakeholder", currentUser.uid],
      }),

      queryClient.invalidateQueries({
        queryKey: ["stakeholderPaymentProfile", currentUser.uid],
      }),
      queryClient.refetchQueries({
        queryKey: ["stakeholderPaymentProfile", currentUser.uid],
      }),
    ]);
  };

  const fetchStakeholderPayments = (): Promise<StakeholderPayment[]> => {
    return new Promise((resolve, reject) => {
      if (!currentUser?.uid) {
        reject(new Error("No authenticated user"));
        return;
      }
      try {
        const paymentsRef = db.stakeholderPayments(currentUser.uid);

        const unsubscribe = onSnapshot(
          paymentsRef,
          (snapshot) => {
            const data = snapshot.docs.map((doc) => {
              const docData = doc.data() as StakeholderPayment;
              return docData;
            });

            resolve(data);
          },
          (error: any) => {
            console.error("Error fetching stakeholder payments:", error);
            reject(error);
          }
        );

        return () => unsubscribe();
      } catch (error) {
        console.error("Error fetching stakeholder payments:", error);
        reject(error);
      }
    });
  };

  const {
    data: stakeholderPayments,
    error: stakeholderPaymentsError,
    isLoading: stakeholderPaymentsLoading,
  } = useQuery({
    queryKey: ["stakeholderPayments", currentUser?.uid],
    queryFn: fetchStakeholderPayments,
    enabled: !!currentUser?.uid,
  });

  const {
    data: paymentProfile,
    error: paymentProfileError,
    isLoading: paymentProfileLoading,
    refetch: refetchPaymentProfile,
  } = useQuery({
    queryKey: ["stakeholderPaymentProfile", currentUser?.uid],
    queryFn: async () => {
      if (!currentUser?.uid) {
        throw new Error("No authenticated user");
      }
      const response =
        await apiClient.bank.stakeholder.getMyStakeholderPaymentProfile();
      if (response.success === false) {
        showError("Failed to fetch payment profile");
        throw new Error(
          response.error?.message || "Failed to fetch payment profile"
        );
      }
      if (response.success === true) {
        return response.data;
      }
      return null;
    },
    enabled: !!currentUser?.uid,
  });

  const {
    data: clientInvoices,
    error: clientInvoicesError,
    isLoading: clientInvoicesLoading,
  } = useQuery({
    queryKey: ["clientInvoices", currentUser?.uid],
    queryFn: async () => {
      if (!currentUser?.uid || !role) {
        throw new Error("No authenticated user");
      }

      const clients = await loadClientsNonAdmin(role, currentUser.uid);

      let invoices: ClientInvoiceData[] = [];

      await Promise.all(
        clients.map(async (client) => {
          const invoicesRef = db.invoices(client.id);
          const invoiceDocs = await getDocs(invoicesRef);
          const data = invoiceDocs.docs.map((doc) => {
            const docData = doc.data();
            return docData;
          });
          invoices = [...invoices, ...data];
        })
      );

      return invoices;
    },
    enabled: !!currentUser?.uid,
  });

  const createMyStakeholderPaymentProfile = async (
    data: Partial<IncreaseExternalAccountDetails> & { nickname: string }
  ) => {
    if (!currentUser?.uid) {
      throw new Error("No authenticated user");
    }

    await apiClient.bank.stakeholder.createStakeholderPaymentProfile(data);

    await invalidatePaymentProfileQueries();
  };

  const updateMyStakeholderPaymentProfile = async (
    data: Partial<IncreaseExternalAccountDetails> & { nickname?: string }
  ) => {
    if (!currentUser?.uid) {
      throw new Error("No authenticated user");
    }

    await apiClient.bank.stakeholder.updateStakeholderPaymentProfile(data);

    await invalidatePaymentProfileQueries();
  };

  const payMyStakeholderInvoice = async (
    clientId: string,
    invoiceId: string,
    amount?: number
  ): Promise<void> => {
    if (!currentUser?.uid) {
      throw new Error("No authenticated user");
    }

    await apiClient.bank.stakeholder.payMyStakeholderInvoice(
      clientId,
      invoiceId,
      amount
    );

    await queryClient.invalidateQueries({
      queryKey: ["clientInvoices", currentUser.uid],
    });
  };

  /**
   * Creates a Stripe checkout session for an invoice payment
   *
   * @param invoice The invoice to pay
   * @param successUrl The URL to redirect to after successful payment
   * @param cancelUrl The URL to redirect to if payment is cancelled
   * @param partialAmount Optional amount for a partial payment
   * @returns The checkout URL, session ID, and fee information
   */
  const createStripeCheckoutSession = async (
    invoice: ClientInvoiceData,
    successUrl: string,
    cancelUrl: string,
    partialAmount?: number
  ): Promise<{
    checkoutUrl: string;
    sessionId: string;
    originalAmount: number;
    processingFee: number;
    totalAmount: number;
  }> => {
    if (!invoice.clientId || !invoice.id) {
      throw new Error("Invalid invoice data");
    }

    const totalAmount = invoice.totalDollarAmountDue || 0;
    const alreadyPaid = invoice.partialPaymentAmount || 0;
    const remainingAmount = totalAmount - alreadyPaid;

    const paymentAmount =
      partialAmount !== undefined ? partialAmount : remainingAmount;

    if (paymentAmount <= 0) {
      throw new Error("Payment amount must be greater than zero");
    }

    if (paymentAmount > remainingAmount) {
      throw new Error(
        `Partial amount cannot exceed the remaining amount due ($${remainingAmount.toFixed(2)})`
      );
    }

    try {
      return apiClient.stripe.stakeholder.createCheckoutSession(
        invoice.id,
        invoice.clientId,
        paymentAmount,
        invoice.invoiceNumber || "Unknown",
        successUrl,
        cancelUrl
      );
    } catch (error) {
      console.error("Stripe checkout error:", error);
      if (error instanceof Error) {
        if (error.message.includes("already paid")) {
          throw new Error("This invoice has already been paid in full");
        }

        if (
          error.message.includes("minimum") ||
          error.message.includes("$0.50")
        ) {
          throw new Error(
            "Stripe requires a minimum payment of $0.50. Please use bank transfer for smaller amounts."
          );
        }
      }
      throw error;
    }
  };

  return {
    paymentProfile,
    paymentProfileError,
    paymentProfileLoading,
    refetchPaymentProfile,
    createMyStakeholderPaymentProfile,
    updateMyStakeholderPaymentProfile,
    clientInvoices,
    clientInvoicesError,
    clientInvoicesLoading,
    payMyStakeholderInvoice,
    stakeholderPayments,
    stakeholderPaymentsError,
    stakeholderPaymentsLoading,
    createStripeCheckoutSession,
  };
};
